import { PDFDocument, StandardFonts, rgb } from "pdf-lib"
import download from "downloadjs"

// In-memory cache so each form fetches its coords JSON only once per session.
const _coordsCache = {}

/**
 * Fetch a per-form coordinate JSON written by extract_form_coords.py
 * (e.g. /forms/falab-medical.coords.json). Returns `null` if not present
 * so the caller can fall back to its hardcoded coords map.
 */
export async function loadCoords(formId) {
    if (_coordsCache[formId] !== undefined) return _coordsCache[formId]
    try {
        const r = await fetch(`/forms/${formId}.coords.json`, { cache: "no-cache" })
        if (!r.ok) {
            _coordsCache[formId] = null
            return null
        }
        const data = await r.json()
        _coordsCache[formId] = data
        return data
    } catch (_e) {
        _coordsCache[formId] = null
        return null
    }
}

/**
 * Fill a blank PDF template by overlaying text at predefined coordinates
 * and trigger a browser download.
 *
 * @param {Object} opts
 * @param {string} opts.templateUrl  URL to the blank PDF (e.g. "/forms/falab-medical.pdf").
 * @param {string} opts.outputName   File name for the downloaded PDF.
 * @param {Object} opts.values       Map of fieldName -> string value (or boolean for checkboxes).
 * @param {Object} opts.coords       Map of fieldName -> { page, x, y, size?, type? }.
 *                                   type defaults to "text"; "check" draws an "X".
 * @param {Array}  [opts.masks]      Optional list of opaque rectangles to draw on
 *                                   top of the template before any values, e.g. to
 *                                   hide a duplicate side-by-side copy in the
 *                                   blank template.
 *                                   Shape: { page, x, y, width, height, color? }
 *                                   where x/y are pdf-lib coords (bottom-left
 *                                   origin) and color defaults to white.
 * @param {boolean} [opts.bold]      Draw text in Helvetica-Bold instead of Helvetica.
 * @param {number}  [opts.xOffset]   Points to shift drawn text rightward (text/textarea
 *                                   only; checkbox "X" marks are left in place).
 * @param {boolean} [opts.removeFormFields] Strip interactive AcroForm fields from the
 *                                   template before drawing, so PDF viewers don't show
 *                                   their highlight boxes over the filled output.
 */
export async function fillAndDownload({ templateUrl, outputName, values, coords, masks = [], bold = false, xOffset = 0, removeFormFields = false }) {
    const bytes = await fetch(templateUrl).then(r => {
        if (!r.ok) throw new Error(`Could not load template ${templateUrl} (${r.status})`)
        return r.arrayBuffer()
    })

    const pdf = await PDFDocument.load(bytes)
    const font = await pdf.embedFont(bold ? StandardFonts.HelveticaBold : StandardFonts.Helvetica)
    const pages = pdf.getPages()

    // Drop leftover interactive form fields so viewers don't paint highlight
    // boxes over the values we draw on top.
    if (removeFormFields) {
        try {
            const form = pdf.getForm()
            form.getFields().forEach(f => {
                try { form.removeField(f) } catch (_e) { /* ignore individual field */ }
            })
        } catch (_e) { /* template has no AcroForm */ }
    }

    // Paint any opacity masks first so values drawn afterwards remain visible.
    masks.forEach(m => {
        const page = pages[m.page ?? 0]
        if (!page) return
        page.drawRectangle({
            x: m.x,
            y: m.y,
            width: m.width,
            height: m.height,
            color: m.color || rgb(1, 1, 1),
            borderWidth: 0,
        })
    })

    Object.entries(coords).forEach(([field, spec]) => {
        const raw = values[field]
        if (raw === undefined || raw === null || raw === "") return

        const page = pages[spec.page ?? 0]
        if (!page) return

        const size = spec.size ?? 10
        const y = spec.y
        const isCheck = spec.type === "check"
        const x = spec.x + (isCheck ? 0 : xOffset)

        if (isCheck) {
            if (raw === true || raw === "true" || raw === "yes" || raw === "X") {
                page.drawText("X", { x, y, size, font, color: rgb(0, 0, 0) })
            }
            return
        }

        const text = String(raw)

        // Naive multi-line support: split on \n and stack downward.
        const lineHeight = size * 1.2
        text.split("\n").forEach((line, i) => {
            page.drawText(line, {
                x,
                y: y - i * lineHeight,
                size,
                font,
                color: rgb(0, 0, 0),
            })
        })
    })

    const out = await pdf.save()
    download(out, outputName, "application/pdf")
}

/**
 * Format a date string/object to DD/MM/YYYY for printing on forms.
 */
export function formatDate(value) {
    if (!value) return ""
    const d = value instanceof Date ? value : new Date(value)
    if (isNaN(d.getTime())) return String(value)
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
}

// Demographics fields can arrive either as plain strings or as wrapped
// objects ({ name } for relations like address/resort, { text } for the
// multiple-input phone entries). Coerce both shapes to a plain string.
function unwrap(v) {
    if (v === undefined || v === null) return ""
    if (typeof v === "object") return v.name || v.text || ""
    return String(v)
}

/**
 * Build a single-line address from demographics fields.
 */
export function buildAddress(d) {
    if (!d) return ""
    const parts = [
        [unwrap(d.address), unwrap(d.address_number)].filter(Boolean).join(" "),
        unwrap(d.resort),
        unwrap(d.district),
    ].filter(Boolean)
    return parts.join(", ")
}

/**
 * Pick the best phone number out of demographics.
 */
export function bestPhone(d) {
    if (!d) return ""
    if (Array.isArray(d.phone_mobile) && d.phone_mobile.length) {
        const mobile = d.phone_mobile.map(unwrap).find(Boolean)
        if (mobile) return mobile
    } else if (d.phone_mobile) {
        const mobile = unwrap(d.phone_mobile)
        if (mobile) return mobile
    }
    return unwrap(d.phone_landline)
}

/**
 * Full name from demographics, with optional title.
 */
export function fullName(d) {
    if (!d) return ""
    const parts = [d.title?.name, d.first_name, d.surname].filter(Boolean)
    return parts.join(" ")
}
